import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
  isNavigationFailure,
} from 'vue-router';

import errorRoutes from '@/views/error/error.router';
import { useAuthStore } from '@/store/auth';
import publicRoutes from '@/views/public/public.router';
import utilityRoutes from '@/views/public/utility.routes';
import dashboardRoutes from '@/views/dashboards/dashboard.router';
import cockpitRoutes from '@/views/cockpits/cockpit.router';
import searchRoutes from '@/views/search/search.router';
import detailRoutes from '@/views/detail/detail.router';
import devRoutes from '@/views/dev/dev.router';
import localStorageHelper from '@/helper/localStorageHelper';
import { getLocale, setLocale } from '@/plugins/i18n';
import { $settings } from '@/provider/settings';
import { authService } from '@/services/auth';

const routeChildren = [
  ...errorRoutes,
  ...publicRoutes,
  ...dashboardRoutes,
  ...cockpitRoutes,
  ...utilityRoutes,
  ...searchRoutes,
  ...detailRoutes,
  ...(import.meta.env.DEV ? devRoutes : []),
];

const router = createRouter({
  history: $settings.isOffice
    ? createMemoryHistory()
    : createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
  routes: [
    {
      path: '/:locale([a-z]{2})?/:cid?',
      component: () => import('@/layouts/FullPage.vue'),
      children: routeChildren,
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/error-404',
    },
  ],
});

const originalPush = router.push;
router.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }

  return originalPush.call(this, location).catch((error) => {
    if (isNavigationFailure(error)) {
      return error;
    }

    throw error;
  });
};

const checkInvalidSettings = () => {
  const requiredSettings = [
    'cid',
    'requestSuffix',
    'middlewareURI',
    'authentication',
  ];

  return requiredSettings.some((setting) => $settings[setting] === undefined);
};

const getCid = (to) => {
  const cidFromStorage = localStorageHelper.getCid();
  const defaultCid = $settings.cid[0].value;
  const cid = cidFromStorage === 'null' ? null : cidFromStorage || defaultCid;

  if (to.name !== undefined && to.params.cid && to.params.cid !== cid) {
    localStorageHelper.setCid(to.params.cid);
    return to.params.cid;
  }

  return cid;
};

export const getDefaultPath = (redirect) => {
  const cid = localStorageHelper.getCid();
  const locale = getLocale();

  if (cid && locale) return { path: `/${locale}/${cid}/${redirect}` };

  return { path: `/login`, query: { redirect } };
};

router.beforeEach(async (to, from, next) => {
  const storedLocale = getLocale();
  const cid = getCid(to);

  // check if URL locale has changed
  if (storedLocale !== to.params.locale && to.params.locale !== undefined) {
    setLocale(to.params.locale);
  }

  if (checkInvalidSettings()) {
    if (to.name !== 'settings-error') router.push({ name: 'settings-error' });
    return next();
  }

  const redirectPath = window.settings.isOffice ? 'search' : 'dashboard/home/';
  const { basicAuth } = useAuthStore();
  const isPublic = to.matched.some((record) => record.meta.public);

  if (to.path === '/' || to.name === undefined)
    return next(getDefaultPath(redirectPath));

  if ([`/${storedLocale}/`, `/${storedLocale}/${cid}/`].includes(to.path))
    return next({ path: `/${storedLocale}/${cid}/${redirectPath}` });

  if (!isPublic) {
    if (!to.params.locale)
      return next({
        path: `/${storedLocale}${to.fullPath}`,
      });

    if (!to.params.cid) {
      const fullPath = to.fullPath.replace(`/${to.params.locale}/`, '');
      return next({ path: `/${storedLocale}/${cid}/${fullPath}` });
    }

    if (!(await authService.isAuthenticated())) {
      const redirect = `/${to.fullPath.split('/').slice(3).join('/')}`;
      return next({
        path: `/${storedLocale}/${cid}/login`,
        query: { redirect },
      });
    }

    if (!basicAuth && $settings.useBasicAuth) {
      const redirect = `/${to.fullPath.split('/').slice(3).join('/')}`;
      return next({
        path: `/${storedLocale}/${cid}/basicAuth`,
        query: { redirect },
      });
    }
  }

  return next();
});

router.afterEach((route) => {
  document.title = route.meta.title || 'itmX webXperience';
  if (!['logout', 'login', 'basicAuth'].includes(route.name)) {
    setTimeout(() => {
      const appLoading = document.querySelector('#loading-bg');
      if (appLoading) {
        appLoading.style.display = 'none';
      }
    }, 300);
  }
});

export default router;
